import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { DateTimeFormatter, dateFormat } from '../../../common/formatter/DateTimeFormatter';
import { FormRenderer } from './FormRenderer';
import { FormAction, FormBlock } from './formDescriptor.types';
import type {
  IDmsDocumentModel,
  IExternalWorkflowErrorViewModel,
  IExternalWorkflowLogEntryViewModel,
  IFormDescriptor,
  IFormDescriptorAction,
  IFormDescriptorBlock,
  IReturnProcessStateResponse,
  IStepView,
} from '../../../generated/ApiClient';

export type ActionValues = Record<string, string | boolean | number | null>;

export type CurrentStepView = Omit<IFormDescriptor, 'actions' | 'blocks'> & {
  actions?: IFormDescriptorAction[];
  blocks?: IFormDescriptorBlock[];
};

export type ReturnProcessStateView = Omit<
  IReturnProcessStateResponse,
  'steps' | 'current' | 'log' | 'error' | 'documents'
> & {
  steps?: IStepView[];
  current?: CurrentStepView;
  log?: IExternalWorkflowLogEntryViewModel[];
  error?: IExternalWorkflowErrorViewModel;
  documents?: IDmsDocumentModel[];
};

interface Props {
  state: ReturnProcessStateView;
  documents?: IDmsDocumentModel[];
  onAction: (actionKey: string, values: ActionValues) => void;
  isTransitioning?: boolean;
  isStale?: boolean;
}

const statusLine: Record<string, string> = {
  Running: 'Der Vorgang wird gerade verarbeitet.',
  Completed: 'Der Vorgang ist abgeschlossen.',
  Cancelled: 'Der Vorgang wurde abgebrochen.',
  Failed: 'Der Vorgang ist fehlgeschlagen.',
};

const notWiredHint = 'Noch nicht angebunden.';

const sideColumnWidth = 280;

const formatMoment = (iso?: string) => {
  if (!iso) return '';
  const parsed = new Date(iso);
  return isNaN(parsed.getTime()) ? iso : DateTimeFormatter.format(parsed);
};

const formatDay = (iso: string) => {
  const parsed = new Date(iso);
  return isNaN(parsed.getTime()) ? iso : DateTimeFormatter.format(parsed, { format: dateFormat });
};

function ProcessStepper({ steps }: { steps: IStepView[] }) {
  const visible = steps.filter((step) => step.state !== 'hidden');
  if (visible.length === 0) return null;

  const activeIndex = visible.findIndex((step) => step.state === 'current');

  return (
    <Stepper
      alternativeLabel
      activeStep={activeIndex === -1 ? visible.length : activeIndex}
      data-testid="step-bar"
      sx={{ width: '100%', mt: 4, mb: 3 }}
    >
      {visible.map((step) => {
        const state = step.state ?? 'open';
        const skipped = state === 'skipped';

        return (
          <Step
            key={step.key}
            completed={state === 'done'}
            disabled={skipped}
            data-testid={`step-${step.key}`}
            data-state={step.state}
          >
            <StepLabel
              optional={
                skipped ? (
                  <Typography variant="caption" color="text.secondary">
                    entfällt
                  </Typography>
                ) : undefined
              }
            >
              <Typography
                variant="caption"
                fontWeight={state === 'current' ? 700 : 400}
                sx={{ textDecoration: skipped ? 'line-through' : undefined }}
              >
                {step.label}
              </Typography>
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
}

function DocumentList({ documents }: { documents: IDmsDocumentModel[] }) {
  return (
    <Box data-testid="document-list" mt={1}>
      {documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Zu diesem Vorgang liegt noch kein Dokument vor.
        </Typography>
      ) : (
        <>
          <Stack divider={<Divider />}>
            {documents.map((document) => (
              <Stack
                key={document.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                py={0.25}
              >
                <Typography variant="body2" noWrap title={document.documentName}>
                  {document.documentName}
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
                  {document.variant && (
                    <Typography variant="caption" color="text.secondary">
                      {document.variant}
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    aria-label={`${document.documentName} öffnen`}
                    title={notWiredHint}
                    disabled
                  >
                    <VisibilityIcon fontSize="small" opacity={0.4} color="secondary" />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.5}
            data-testid="document-preview-not-wired"
          >
            Die Vorschau ist noch nicht angebunden.
          </Typography>
        </>
      )}
    </Box>
  );
}

function History({ log }: { log: IExternalWorkflowLogEntryViewModel[] }) {
  const entries = useMemo(
    () =>
      [...log].sort((a, b) => {
        const left = a.at ?? '';
        const right = b.at ?? '';
        return left < right ? 1 : left > right ? -1 : 0;
      }),
    [log]
  );

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" mt={1}>
        Noch keine Einträge.
      </Typography>
    );
  }

  return (
    <Stack gap={1} mt={1} data-testid="history" sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
      {entries.map((entry, index) => (
        <Box key={`${entry.at}-${index}`}>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatMoment(entry.at)} · {entry.actor}
          </Typography>
          <Typography variant="body2">{entry.text}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

function NoteBox() {
  return (
    <Stack gap={1} mt={1}>
      <TextField
        size="small"
        multiline
        minRows={2}
        label="Bemerkung"
        defaultValue=""
        disabled
        helperText="Bemerkungen sind noch nicht angebunden."
        data-testid="note-box"
      />
      <Box textAlign="right">
        <Button variant="outlined" color="secondary" size="small" disabled title={notWiredHint}>
          Bemerkung speichern
        </Button>
      </Box>
    </Stack>
  );
}

export function ReturnProcessTab({
  state,
  documents = [],
  onAction,
  isTransitioning,
  isStale,
}: Props) {
  const current = state.current;
  const [pending, setPending] = useState<
    { actionKey: string; values: ActionValues; confirmText: string } | undefined
  >();

  const currentForm = current as { actions?: FormAction[]; blocks?: FormBlock[] } | undefined;

  const yourTurn = !!current?.actions?.some((action) => action.allowed);
  const waitingLine = yourTurn ? '' : current?.waitingForLabel ?? statusLine[state.status] ?? '';

  const handleSubmit = (actionKey: string, values: ActionValues) => {
    const confirmText = current?.actions?.find((action) => action.key === actionKey)?.confirmText;
    if (confirmText) {
      setPending({ actionKey, values, confirmText });
      return;
    }
    onAction(actionKey, values);
  };

  const confirmPending = () => {
    if (pending) onAction(pending.actionKey, pending.values);
    setPending(undefined);
  };

  return (
    <Box width="100%" sx={{ pb: 20 }} data-testid="return-process-tab">
      <ProcessStepper steps={state.steps ?? []} />

      {isStale && (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="return-process-stale">
          Der Stand konnte gerade nicht aktualisiert werden. Angezeigt wird der letzte bekannte
          Stand.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={4} alignItems="flex-start" mt={2}>
        <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%', position: 'relative' }}>
          {waitingLine && (
            <Typography variant="subtitle1" color="text.secondary" data-testid="status-line">
              {waitingLine}
            </Typography>
          )}

          {state.error && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="process-error">
              {state.error.text}
            </Alert>
          )}

          {current && (
            <Box data-testid="current-step">
              <div className="section-title">{current.title}</div>

              {current.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {current.subtitle}
                </Typography>
              )}
              {current.deadlineAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Frist: {formatDay(current.deadlineAt)}
                </Typography>
              )}

              <Box
                mt={2}
                sx={{
                  opacity: isTransitioning ? 0.4 : 1,
                  pointerEvents: isTransitioning ? 'none' : 'auto',
                  transition: 'opacity 150ms',
                }}
              >
                <FormRenderer
                  key={current.correlationKey}
                  blocks={currentForm?.blocks ?? []}
                  actions={currentForm?.actions ?? []}
                  onSubmit={handleSubmit}
                  busy={isTransitioning}
                />
              </Box>
            </Box>
          )}

          {isTransitioning && (
            <Stack
              data-testid="transition-veil"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{ position: 'absolute', inset: 0 }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Der Schritt wird übernommen …
              </Typography>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            width: '100%',
            flex: { md: `0 0 ${sideColumnWidth}px` },
            maxWidth: { md: sideColumnWidth },
          }}
        >
          <div className="section-title">Dokumente</div>
          <DocumentList documents={documents} />

          <div className="line" />
          <div className="section-title">Historie</div>
          <History log={state.log ?? []} />

          <div className="line" />
          <div className="section-title">Bemerkungen</div>
          <NoteBox />
        </Box>
      </Stack>

      <Dialog open={!!pending} onClose={() => setPending(undefined)}>
        <DialogTitle>Bitte bestätigen</DialogTitle>
        <DialogContent>
          <DialogContentText>{pending?.confirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setPending(undefined)}>
            Abbrechen
          </Button>
          <Button variant="contained" color="primary" onClick={confirmPending}>
            Fortfahren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
